import moment, { type Moment } from "moment";
import { isRecord } from "../../../types/internal/utility";

/**
 * Compare two object together
 * Uses exact ("===") comparison
 * @returns True if they are the same, False otherwise
 */
export function compare(a: unknown, b: unknown): boolean {
    if(typeof a !== typeof b) {
        return false;
    }

    b = b as typeof a;

    if(Array.isArray(a) && Array.isArray(b)) {
        if(a.length !== b.length) {
            return false;
        }

        for(let i = 0, l = a.length; i < l; i++) {
            if(!compare(a[i], b[i])) {
                return false;
            }
        }
    } else if(typeof a === "object" && typeof b === "object") {
        if(a === null && b === null) {
            return true;
        } else if(a === null || b === null) {
            return false;
        }

        if(Object.keys(a).length !== Object.keys(b).length) {
            return false;
        }

        for(const i in a) {
            if(!Object.prototype.hasOwnProperty.call(a, i)) continue;

            if(!compare(a[i as keyof typeof a], b[i as keyof typeof b])) {
                return false;
            }
        }
    } else if(typeof a === "number" && typeof b === "number") {
        if(isNaN(a) && isNaN(b)) {
            return true;
        }

        return a === b;
    } else {
        return a === b;
    }

    return true;
}

/**
 * Check if an Object is empty
 */
export function isEmpty(toTest: {}): boolean {
    for(const i in toTest) {
        if(Object.prototype.hasOwnProperty.call(toTest, i)) {
            return false;
        }
    }

    return true;
}

/**
 * Calculate a score based off the differences of two objects, with each change being worth one
 */
export function diffScore(a: unknown, b: unknown): number {
    if(typeof a !== typeof b) {
        return 1;
    }

    if(Array.isArray(a) && Array.isArray(b)) {
        let score = 0;

        // Loop through the array and add to the score
        for(let i = 0, l = a.length; i < l; i++) {
            score += diffScore(a[i], b[i]);
        }

        // Add major counts through internal objects and arrays
        for(let i = a.length, l = b.length; i < l; i++) {
            if(Array.isArray(b[i])) {
                score += b.length;
            } else if(typeof b[i] === "object" && b[i] !== null) {
                score += Object.keys(b[i]).length;
            } else {
                score += 1;
            }
        }

        // Return the score
        return score;
    } else if(typeof a === "object" && typeof b === "object") {
        if(a === null && b === null) {
            return 0;
        }

        if(a === null || b === null) {
            return 1;
        }

        if(a instanceof moment) {
            if(b instanceof moment) {
                return (a as Moment).isSame(b) ? 0 : 1;
            } else {
                return 1;
            }
        }

        let score = Object.keys(b).length - Object.keys(a).length;

        for(const i in a) {
            if(!Object.prototype.hasOwnProperty.call(a, i)) continue;

            if(typeof b[i as keyof typeof b] === "undefined") {
                score += 1;
                continue;
            }

            score += diffScore(a[i as keyof typeof a], b[i as keyof typeof a]);
        }

        return score;
    } else if(a !== b) {
        return 1;
    }

    return 0;
}

/**
 * Perform a deep update of an object, returning a new version of the object, not mutating the existing object
 */
export const deepUpdate = <TState extends Record<string, unknown>>(
    state: TState,
    keyPath: Array<string | number> | string,
    value: unknown
): TState => {
    if(Array.isArray(keyPath)) {
        const path = [ ...keyPath ];
        const key = path.pop();

        if(typeof key === "undefined") {
            return state;
        }

        const newState = { ...state };

        let layer: Record<string | number, unknown> | Array<unknown> = newState;

        for(let i = 0, l = path.length; i < l; i++) {
            const pathKey = path[i];

            let newLayer: Record<string | number, unknown> | Array<unknown>;

            if(pathKey in layer) {
                // Check to see path can continue to be traversed
                if(Array.isArray(layer[pathKey as number])) {
                    newLayer = [
                        ...(layer[pathKey as number] as Array<unknown>),
                    ];
                } else if(!isRecord(layer[pathKey as number])) {
                    console.error("Unable to process change for field", keyPath);

                    return state;
                } else {
                    newLayer = {
                        ...(layer[pathKey as number] as Record<string, unknown>),
                    };
                }
            } else {
                newLayer = {};
            }

            // Update the item on the path
            layer[pathKey as number] = newLayer;
            layer = newLayer;
        }

        layer[key as number] = value;

        return newState;
    }

    return {
        ...state,
        [keyPath]: value,
    };
};

/**
 * Perform an update on the provided state, ensure no mutations of data while traversing through
 * a key path
 */
export const deepStateUpdate = <TState extends Record<string, unknown> | null>(
    keyPath: Array<string> | string,
    value: unknown
): (state: TState) => TState => {
    return state => {
        if(state === null) return state;

        return deepUpdate(state, keyPath, value);
    };
};
